import { useCallback, useContext, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { ReportFormat } from '@trinity/chat';
import {
  QuantAnalysisContext,
  QuantAnalysisVersionContext,
  SetFolderCreationModeContext,
} from '@trinity/chat-components';
import { PopperMenu, PopperMenuItem } from '@trinity/chat-components/components/Popper/index.js';
import { Portal } from '@trinity/chat-components/components/Portal/index.js';
import { Button } from '@trinity/chat-components/components/presentation/Button/index.js';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming/Context.js';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useAsyncMutationDownloader } from '@trinity/chat-components/containers/QueryDownloader/index.js';
import { useDeleteQuantAnalysisModal } from '@trinity/chat-components/hooks/useDeleteQuantAnalysisModal.js';
import { useUploadQuantAnalysisFileModal } from '@/containers/QuantAnalysis/hooks';
import { ProjectDetailsContext } from '@/screens/Project.Main/Context';

type Props = {
  className?: string;
};

export const QuantAnalysisActions = ({ className }: Props) => {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);

  const theming = useContext(AppThemingContext);
  const quantAnalysis = useContext(QuantAnalysisContext);
  const version = useContext(QuantAnalysisVersionContext);
  const projectDetails = useContext(ProjectDetailsContext);

  const enabled = theming?.theme?.enabled;
  const primaryColor = enabled ? theming?.palette?.primary?.main : 'var(--pri-02)';
  const hoverBackgroundColor = enabled ? theming?.palette?.primary?.light : 'var(--blue-l)';

  const projectId = projectDetails.query.data?.project?.id;

  const handleBlur = useCallback(() => {
    setOpen(false);
  }, []);

  const handleClick = useCallback(() => {
    setOpen(true);
  }, []);

  const setFolderCreationMode = useContext(SetFolderCreationModeContext);
  const navigate = useNavigate();

  const [toggleDeleteQuantAnalysisModal, DeleteQuantAnalysisModal] = useDeleteQuantAnalysisModal({
    projectId,
    quantAnalysisId: quantAnalysis.id,
    onSuccess: () => {
      const projectSlug = projectDetails.query.data?.project?.id?.toString();
      if (projectSlug) {
        navigate(`/projects/${projectSlug}`);
      } else {
        navigate(`/projects/${projectId}`);
      }
    },
  });

  const [toggleQuantAnalysisFileUpload, UploadQuantAnalysisFileModal] = useUploadQuantAnalysisFileModal({
    projectId,
    quantAnalysisId: quantAnalysis.id,
  });

  const apiClient = useChatApiClient();

  const { mutateAsync: startReparsingFile } = useAsyncMutationDownloader({
    mutationFn: async () => {
      const response = await apiClient.quantAnalysis.reparseQuantAnalysis({
        projectId,
        quantAnalysisId: quantAnalysis.id,
        versionNumber: version.versionNumber,
      });
      return response.data;
    },
  });

  const handleReparseFile = useCallback(() => {
    const format = ReportFormat.Excel;
    startReparsingFile({
      meta: {
        format,
        name: quantAnalysis.name,
        title: `Uploading Analysis File`,
      },
    });
    handleBlur();
  }, [handleBlur, quantAnalysis, startReparsingFile]);

  const handleNewVersionFile = useCallback(() => {
    toggleQuantAnalysisFileUpload();
    handleBlur();
  }, [handleBlur, toggleQuantAnalysisFileUpload]);

  const handleDeleteAnalysis = useCallback(() => {
    toggleDeleteQuantAnalysisModal();
    handleBlur();
  }, [handleBlur, toggleDeleteQuantAnalysisModal]);

  const handleAddFolder = useCallback(() => {
    if (!setFolderCreationMode) {
      console.error('SetFolderCreationModeContext is null - context provider not found');
      return;
    }
    setFolderCreationMode(true);
    handleBlur();
  }, [setFolderCreationMode, handleBlur]);

  return (
    <>
      <div ref={buttonRef} className={className} onClick={handleClick}>
        <StyledButton variant="brick" primaryColor={primaryColor} hoverBackgroundColor={hoverBackgroundColor}>
          Actions
        </StyledButton>
      </div>
      {open && (
        <Portal>
          <ClickAwayListener mouseEvent="onMouseDown" onClickAway={handleBlur} touchEvent="onTouchStart">
            <div
              style={{
                position: 'absolute',
                top: buttonRef.current ? buttonRef.current.getBoundingClientRect().bottom + 5 : 0,
                left: buttonRef.current ? buttonRef.current.getBoundingClientRect().left : 0,
                zIndex: 1000,
              }}
            >
              <PopperMenu>
                <PopperMenuItem onClick={handleDeleteAnalysis}>Delete Analysis</PopperMenuItem>
                <PopperMenuItem onClick={handleReparseFile}>Reparse File</PopperMenuItem>
                <PopperMenuItem onClick={handleNewVersionFile}>Upload File</PopperMenuItem>
                <PopperMenuItem onClick={handleAddFolder}>Add Folder</PopperMenuItem>
              </PopperMenu>
            </div>
          </ClickAwayListener>
        </Portal>
      )}
      <DeleteQuantAnalysisModal />
      {UploadQuantAnalysisFileModal}
    </>
  );
};

const StyledButton = styled(Button.Secondary)<{
  primaryColor: string;
  hoverBackgroundColor: string;
}>`
  color: ${(props) => props.primaryColor} !important;
  border-color: ${(props) => props.primaryColor} !important;

  &:hover {
    color: ${(props) => props.primaryColor} !important;
    background-color: ${(props) => props.hoverBackgroundColor} !important;
  }
`;

export default QuantAnalysisActions;
